/** RFC-227 — one application review with one decision row per grant. */
import { useEffect, useMemo, useState } from 'react'
import { format } from 'date-fns'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Link,
  createFileRoute,
  useNavigate,
  useParams,
} from '@tanstack/react-router'
import type {
  AccessReviewDTO,
  AccessReviewEntitlementDTO,
  AccessReviewItemDTO,
  AccessReviewPrivilegeTier,
} from '@deepguide-ai/types'
import { Badge } from '@deepguide-ai/ui/badge'
import { Button } from '@deepguide-ai/ui/button'
import { Checkbox } from '@deepguide-ai/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@deepguide-ai/ui/dialog'
import { Input } from '@deepguide-ai/ui/input'
import { Label } from '@deepguide-ai/ui/label'
import { Textarea } from '@deepguide-ai/ui/textarea'
import { ToggleGroup, ToggleGroupItem } from '@deepguide-ai/ui/toggle-group'
import { Progress } from '@deepguide-ai/ui/progress'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@deepguide-ai/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@deepguide-ai/ui/select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@deepguide-ai/ui/dropdown-menu'
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  ChevronRight,
  Download,
  Image as ImageIcon,
  LoaderCircle,
  MoreHorizontal,
  RefreshCw,
  Search,
  Upload,
} from 'lucide-react'
import { toast } from 'sonner'
import { usePageHeaderTitle } from '@/lib/page-header'
import { trpc, trpcClient } from '@/lib/trpc'
import { downloadBlob } from '@/lib/download-file'
import { useWorkspacePermissions } from '@/hooks/useWorkspacePermissions'
import {
  AccessReviewSteps,
  type AccessReviewStep,
} from '@/components/compliance/access-reviews/AccessReviewSteps'
import { HeaderField } from '@/components/compliance/access-reviews/HeaderField'
import { AppLogo } from '@/components/compliance/access-reviews/AppLogo'
import { InfoNote } from '@/components/compliance/access-reviews/InfoNote'
import { ConfirmDialog } from '@/components/confirm-dialog'
import { UserSelect, type UserSelectMember } from '@/components/ui/user-select'
import {
  MemberAvatar,
  type MemberAvatarPerson,
} from '@/components/compliance/MemberAvatar'
import { AddUnknownPrincipalDialog } from '@/components/compliance/people/AddUnknownPrincipalDialog'
import { CustomRestConnectionButton } from '@/components/compliance/people/CustomRestIntegrationWizard'
import {
  ImportCustomAppUsersDialog,
  type ImportMode,
} from '@/components/compliance/people/ImportCustomAppUsersDialog'
import { MarkServiceAccountDialog } from '@/components/compliance/people/MarkServiceAccountDialog'
import { PageHeaderBreadcrumb } from '@/components/layout/PageHeaderActions'

export const Route = createFileRoute(
  '/(protected)/workspaces/$workspaceId/reviews/access_/$cycleId'
)({
  component: AccessReviewCyclePage,
  staticData: { pageHeader: { title: 'Access Review' } },
})

// Two outcomes: keep or remove. Older rows may still carry ACCEPT_RISK /
// MODIFY; they render as a badge but can't be chosen any more.
type ItemDecision = 'CERTIFY' | 'REVOKE' | 'PENDING_DECISION'
/**
 * The decisions that flag a grant for change in the real application, and so
 * the only ones that carry a remediation record. Mirrors the server's
 * `REMEDIABLE_DECISIONS`.
 */
const DENIED_DECISIONS = new Set(['REVOKE', 'MODIFY'])
// Header bulk menu: the two decisions plus classification, which opens the
// shared service-account dialog for every selected row that has a principal id.
type BulkAction = ItemDecision | 'MARK_SERVICE_ACCOUNT'
const DECISION_DONE: Record<ItemDecision, string> = {
  CERTIFY: 'certified',
  REVOKE: 'denied',
  PENDING_DECISION: 'returned to pending',
}

const PRIVILEGED_RULES = new Set([
  'privileged',
  'sod_conflict',
  'dormant_admin',
])

function AccessReviewCyclePage() {
  const { workspaceId, cycleId } = useParams({ strict: false }) as {
    workspaceId: string
    cycleId: string
  }
  const { canWorkspaceWrite } = useWorkspacePermissions(workspaceId)
  const navigate = useNavigate()
  const cycleQuery = useQuery({
    ...trpc.accessReview.get.queryOptions({ id: cycleId }),
    refetchInterval: (query) =>
      query.state.data?.populationState === 'COLLECTING' ? 1_500 : false,
  })
  const cycle = cycleQuery.data
  const periodQuery = useQuery({
    ...trpc.accessReview.period.queryOptions({
      periodKey: cycle?.periodKey ?? '',
    }),
    enabled: Boolean(cycle?.periodKey),
  })
  const entitlementQuery = useQuery(
    trpc.accessReview.entitlementConfiguration.queryOptions({ cycleId })
  )
  const membersQuery = useQuery(trpc.workspaceMembers.list.queryOptions())
  // A period review is one application, so its items need no source filter.
  const itemsQuery = useQuery({
    ...trpc.accessReview.itemsForApp.queryOptions({ cycleId }),
    enabled: Boolean(cycle),
    refetchInterval: (query) => {
      const fetchedItemCount = query.state.data?.length ?? 0
      // A legacy bulk-attested cycle has accounts but no per-principal rows;
      // give the item query two passes, then stop.
      const collecting =
        cycle?.populationState === 'COLLECTING' ||
        (cycle?.populationState === 'POPULATED' &&
          fetchedItemCount === 0 &&
          query.state.dataUpdateCount < 2)
      return collecting ? 1_500 : false
    },
  })
  const items = (itemsQuery.data ?? []) as AccessReviewItemDTO[]
  const [importMode, setImportMode] = useState<ImportMode | null>(null)
  const [viewedStep, setViewedStep] = useState<AccessReviewStep | null>(null)
  const [reopenOpen, setReopenOpen] = useState(false)
  const queryClient = useQueryClient()

  useEffect(() => {
    if (cycle?.populationState === 'POPULATED') {
      void queryClient.invalidateQueries({
        queryKey: trpc.accessReview.entitlementConfiguration.queryKey({ cycleId }),
      })
    }
  }, [cycleId, cycle?.populationState, cycle?.populationReconciledAt, queryClient])

  const invalidate = async () => {
    // Any mutation may move the wizard forward (mapping confirmed, accounts
    // imported); drop the explicitly viewed step so it follows the state.
    setViewedStep(null)
    await queryClient.invalidateQueries({ queryKey: trpc.accessReview.pathKey() })
  }
  const reopenMutation = useMutation({
    ...trpc.accessReview.reopenSteps.mutationOptions(),
    onError: (error: Error) => toast.error(error.message),
    onSuccess: async (result) => {
      setReopenOpen(false)
      toast.success(
        result.resetDecisions
          ? `Earlier steps reopened, ${result.resetDecisions} decision${result.resetDecisions === 1 ? '' : 's'} reset`
          : 'Earlier steps reopened'
      )
      await invalidate()
    },
  })
  const requestPopulationMutation = useMutation({
    ...trpc.accessReview.requestPopulation.mutationOptions(),
    onError: (error: Error) => toast.error(error.message),
    onSuccess: async () => {
      toast.success('Collecting accounts…')
      await invalidate()
    },
  })
  const updateMutation = useMutation({
    ...trpc.accessReview.updateItemDecision.mutationOptions(),
    onError: (error: Error) => toast.error(error.message),
    onSuccess: invalidate,
  })
  const bulkMutation = useMutation({
    ...trpc.accessReview.bulkUpdateDecisions.mutationOptions(),
    onError: (error: Error) => toast.error(error.message),
    onSuccess: async (result) => {
      toast.success(`Certified ${result.updated} routine grants`)
      await invalidate()
    },
  })
  // Selection-based decisions: one batch call, same per-grant guardrails as
  // a single decision (all-or-nothing). The routine "Certify all" keeps using
  // the separate audited bulk endpoint.
  const selectionMutation = useMutation({
    ...trpc.accessReview.updateItemDecisions.mutationOptions(),
    onError: (error: Error) => toast.error(error.message),
  })
  const applyBulkDecision = async (
    itemIds: string[],
    decision: ItemDecision,
    notes?: string
  ) => {
    const result = await selectionMutation
      .mutateAsync({ reviewId: cycleId, itemIds, decision, notes })
      .catch(() => null)
    if (result) {
      toast.success(
        `${result.updated} grant${result.updated === 1 ? '' : 's'} ${DECISION_DONE[decision]}`
      )
    }
    await invalidate()
  }
  const remediationMutation = useMutation({
    ...trpc.accessReview.recordItemRemediation.mutationOptions(),
    onError: (error: Error) => toast.error(error.message),
    onSuccess: async (_result, variables) => {
      toast.success(
        variables.remediated
          ? 'Remediation recorded'
          : 'Remediation reopened'
      )
      await invalidate()
    },
  })
  const confirmEntitlementsMutation = useMutation({
    ...trpc.accessReview.confirmEntitlementConfiguration.mutationOptions(),
    onError: (error: Error) => toast.error(error.message),
    onSuccess: async () => {
      toast.success('Role and privilege-tier mapping confirmed')
      await invalidate()
    },
  })
  // Shell header: Access Reviews › {review} › {application} Access Review.
  usePageHeaderTitle(
    cycle ? `${cycle.scopeName ?? cycle.name} Access Review` : null
  )

  if (cycleQuery.isLoading)
    return <PageMessage>Loading access review…</PageMessage>
  if (!cycle) return <PageMessage>Access review not found.</PageMessage>

  const readOnly = !canWorkspaceWrite || cycle.evidenceCleanupPending === true || cycle.periodSigned === true || cycle.status === 'COMPLETED' || cycle.status === 'CANCELLED'
  const applicationName = cycle.scopeName ?? cycle.name
  const resolvedCount = items.filter(
    (item) => item.decision !== 'PENDING_DECISION'
  ).length
  // Collection state is derived server-side (a failed or timed-out sync is
  // FAILED, never an endless spinner); the only client-side wait is the
  // moment between accounts existing and the item query catching up.
  const populationState = cycle.populationState ?? 'NOT_STARTED'
  const populationError =
    populationState === 'FAILED' ? (cycle.populationError ?? null) : null
  const populationLoading =
    populationState === 'COLLECTING' ||
    (populationState === 'POPULATED' && items.length === 0 && itemsQuery.isLoading)
  const populated =
    populationState === 'POPULATED' ||
    cycle.status === 'COMPLETED' ||
    items.length > 0
  const reviewerMembers = (membersQuery.data ?? [])
    .filter((member) => member.user !== null && member.role !== 'AUDITOR')
    .map((member) => ({ user: member.user! }))
  const populationDate = cycle.populationReconciledAt
    ? format(new Date(cycle.populationReconciledAt), 'MMM d, yyyy HH:mm')
    : null
  const reviewer: MemberAvatarPerson = reviewerMembers.find(
    (member) => member.user.id === cycle.reviewerUserId
  )?.user ?? { name: cycle.reviewerName ?? 'Unassigned' }
  const reviewStatus: { label: string; status: 'default' | 'blue' | 'green' } =
    cycle.status === 'COMPLETED'
      ? { label: 'Completed', status: 'green' }
      : cycle.editing ? { label: 'Editing', status: 'blue' }
      : resolvedCount > 0
        ? { label: 'In progress', status: 'blue' }
        : { label: 'Not started', status: 'default' }
  const entitlementConfiguration = entitlementQuery.data
  const entitlementsConfirmed =
    cycle.status === 'COMPLETED' ||
    Boolean(entitlementConfiguration?.confirmedAt)
  // Wizard: the furthest step the review's state allows, and the step being
  // viewed. Viewing auto-advances when the state moves forward (accounts
  // land, mapping confirmed) but never jumps the reviewer backwards.
  const reachedStep: AccessReviewStep =
    cycle.status === 'COMPLETED'
      ? 4
      : !populated
        ? 1
        : !entitlementsConfirmed
          ? 2
          : // Until the grants have loaded, "all decided" is not known yet —
            // don't flash the Complete step on first paint.
            itemsQuery.isLoading || resolvedCount < items.length
            ? 3
            : 4
  // A step the reviewer picked stays only while the state still allows it.
  // Deciding the last grant unlocks Complete but never jumps to it — the
  // reviewer moves on when they are done looking at the grants.
  const step: AccessReviewStep =
    viewedStep && viewedStep <= reachedStep
      ? viewedStep
      : reachedStep === 4 && cycle.status !== 'COMPLETED'
        ? 3
        : reachedStep
  // Steps 1–2 are read-only once the mapping is confirmed (decisions depend
  // on it); reopening is explicit and resets the decisions.
  const stepsLocked =
    entitlementsConfirmed && cycle.status !== 'COMPLETED'

  return (
    <div className='font-body'>
      <PageHeaderBreadcrumb>
        <Link
          to='/workspaces/$workspaceId/reviews/access'
          params={{ workspaceId }}
          className='font-heading text-text-subtle text-[15px] font-medium hover:text-cobalt-strong'
        >
          Access Reviews
        </Link>
        {cycle.periodKey && periodQuery.data ? (
          <>
            <ChevronRight
              className='text-text-placeholder h-3.5 w-3.5 shrink-0'
              aria-hidden
            />
            <Link
              to='/workspaces/$workspaceId/reviews/access/period/$periodKey'
              params={{ workspaceId, periodKey: cycle.periodKey }}
              className='font-heading text-text-subtle max-w-[280px] truncate text-[15px] font-medium hover:text-cobalt-strong'
            >
              {periodQuery.data.label}
            </Link>
          </>
        ) : null}
      </PageHeaderBreadcrumb>

      <header className='mb-5'>
        <div className='flex items-start justify-between gap-6'>
          <h1 className='text-text-secondary flex items-center gap-2.5 text-[24px] font-semibold tracking-[-0.02em]'>
            <AppLogo name={applicationName} size='md' />
            {applicationName} Access Review
          </h1>
        </div>
        <div className='mt-3 grid gap-4 sm:grid-cols-3'>
          <HeaderField label='Status'>
            <Badge status={reviewStatus.status} type='dot'>
              {reviewStatus.label}
            </Badge>
          </HeaderField>
          <HeaderField label='Reviewer'>
            <span className='flex min-w-0 items-center gap-2'>
              <MemberAvatar person={reviewer} size='sm' />
              <span className='text-text-secondary truncate text-[12px] font-medium'>
                {cycle.reviewerName ?? 'Unassigned'}
              </span>
            </span>
          </HeaderField>
          <HeaderField label='Progress'>
            <span className='flex items-center gap-2'>
              <Progress
                value={items.length ? (resolvedCount / items.length) * 100 : 0}
                className='h-1.5 w-24'
                aria-label='Grants decided'
              />
              <span className='text-text-secondary text-[12px] tabular-nums'>
                {resolvedCount}/{items.length} decided
              </span>
            </span>
          </HeaderField>
        </div>
      </header>

      <AccessReviewSteps
        currentStep={step}
        reachedStep={reachedStep}
        onSelect={setViewedStep}
      />

      {stepsLocked && step < 3 && !readOnly ? (
        <InfoNote
          className='mb-4'
          action={
            <Button
              size='sm'
              variant='outline'
              className='bg-card'
              onClick={() => setReopenOpen(true)}
            >
              Reopen earlier steps
            </Button>
          }
        >
          The role mapping is confirmed, so this step is read-only. Reopening
          resets the {resolvedCount} decision{resolvedCount === 1 ? '' : 's'}{' '}
          made so far.
        </InfoNote>
      ) : null}

      {step === 1 ? (
        <>
          {cycle.scopeType === 'CUSTOM' && cycle.scopeId && !readOnly && !entitlementsConfirmed ? (
            <section className='border-border mb-4 flex flex-wrap items-center justify-between gap-4 rounded-md border bg-card px-5 py-5'>
              <div>
                <h2 className='text-text-secondary text-[12px] font-semibold'>
                  Connect an accounts API
                </h2>
                <p className='text-text-subtle mt-1 text-[11px] leading-5'>
                  Pull accounts and roles from {applicationName} using an API key.
                </p>
              </div>
              <CustomRestConnectionButton
                app={{ id: cycle.scopeId, name: applicationName }}
              />
            </section>
          ) : null}
          {populationError ? (
            <PopulationFailed
              applicationName={applicationName}
              message={populationError.message}
              canRetry={!readOnly && !entitlementsConfirmed}
              retrying={requestPopulationMutation.isPending}
              onRetry={() =>
                requestPopulationMutation.mutate({ reviewId: cycleId })
              }
              onImport={() => setImportMode('upload')}
            />
          ) : populationLoading ? (
            <PopulationLoading applicationName={applicationName} />
          ) : itemsQuery.isLoading ? (
            <PageMessage>Loading accounts…</PageMessage>
          ) : populated ? (
            <PopulationSummary
              accountCount={items.length}
              method={cycle.populationMethod ?? null}
              collectedAt={populationDate}
              recollectMethods={
                !readOnly && !entitlementsConfirmed
                  ? (cycle.populationMethods ?? [])
                  : []
              }
              syncing={requestPopulationMutation.isPending}
              onSync={() =>
                requestPopulationMutation.mutate({ reviewId: cycleId })
              }
              onImport={setImportMode}
            />
          ) : (
            <CollectAccounts
              applicationName={applicationName}
              methods={
                populationState === 'NOT_STARTED' && !readOnly && !entitlementsConfirmed
                  ? (cycle.populationMethods ?? [])
                  : []
              }
              syncing={requestPopulationMutation.isPending}
              onSync={() =>
                requestPopulationMutation.mutate({ reviewId: cycleId })
              }
              onImport={setImportMode}
            />
          )}
          {populated ? (
            <WizardNav onNext={() => setViewedStep(2)} nextLabel='Map roles' />
          ) : null}
        </>
      ) : null}

      {step === 2 ? (
        <>
          {entitlementQuery.isError ? (
            <section className='rounded-md border bg-card p-4 text-sm'>
              <p>Could not load role mappings.</p>
              <Button variant='outline' onClick={() => void entitlementQuery.refetch()}>Retry</Button>
            </section>
          ) : entitlementQuery.isFetching || !entitlementConfiguration ? (
            <PageMessage>Loading role mappings…</PageMessage>
          ) : entitlementConfiguration.entitlements.length ? (
            <EntitlementConfirmation
              entitlements={entitlementConfiguration.entitlements}
              confirmedAt={entitlementConfiguration.confirmedAt}
              readOnly={readOnly}
              saving={confirmEntitlementsMutation.isPending}
              members={reviewerMembers}
              onConfirm={(entitlements) =>
                confirmEntitlementsMutation.mutate({ cycleId, entitlements })
              }
            />
          ) : (
            <section className='border-border text-text-subtle rounded-md border border-dashed bg-card px-5 py-4 text-[11px]'>
              No roles were reported for these accounts; every grant maps to
              one tier.
              {!readOnly && !entitlementsConfirmed ? (
                <Button className='mt-3 block' size='sm' variant='outline'
                  disabled={confirmEntitlementsMutation.isPending}
                  onClick={() => confirmEntitlementsMutation.mutate({ cycleId, entitlements: [] })}>
                  Confirm mapping
                </Button>
              ) : null}
            </section>
          )}
          <WizardNav
            onBack={() => setViewedStep(1)}
            onNext={entitlementsConfirmed ? () => setViewedStep(3) : undefined}
            nextLabel='Review grants'
          />
        </>
      ) : null}

      {step === 3 ? (
        <>
          <InfoNote className='mb-3'>
            Deny records the desired state only. Screenata never changes{' '}
            {applicationName}. Apply denied and modified grants there using the
            decision record on the Complete step.
          </InfoNote>
          <AccountReview
            items={items}
            readOnly={readOnly}
            updating={
              updateMutation.isPending ||
              bulkMutation.isPending ||
              selectionMutation.isPending
            }
            onDecision={(itemId, decision, notes) =>
              updateMutation.mutate({ itemId, decision, notes })
            }
            onBulkDecision={applyBulkDecision}
            onCertifyRoutine={(routine) =>
              bulkMutation.mutate({
                reviewId: cycleId,
                decisions: routine.map((item) => ({
                  id: item.id,
                  decision: 'CERTIFY',
                })),
              })
            }
            onRefresh={invalidate}
          />
          <WizardNav
            onBack={() => setViewedStep(2)}
            onNext={
              resolvedCount === items.length
                ? () => setViewedStep(4)
                : undefined
            }
            nextLabel='Complete application'
            nextHint={
              resolvedCount < items.length
                ? `${items.length - resolvedCount} grant${items.length - resolvedCount === 1 ? '' : 's'} still to decide`
                : undefined
            }
          />
        </>
      ) : null}

      {step === 4 ? (
        <>
          <DecisionRecord
            cycleId={cycleId}
            items={items}
            reviewerNames={
              new Map(
                reviewerMembers.map((member) => [
                  member.user.id,
                  member.user.name ?? member.user.email ?? member.user.id,
                ])
              )
            }
            canRemediate={!readOnly && cycle.periodSigned === false}
            remediating={remediationMutation.isPending}
            onRemediate={(itemId, remediated, notes) =>
              remediationMutation.mutate({ itemId, remediated, notes })
            }
          />
          <CompleteApplication
            cycleId={cycleId}
            status={cycle.status}
            periodSigned={cycle.periodSigned === true}
            grantCount={items.length}
            pendingCount={items.length - resolvedCount}
            canWrite={canWorkspaceWrite}
            onCompleted={async () => {
              await invalidate()
              if (cycle.periodKey) {
                await queryClient.invalidateQueries({
                  queryKey: trpc.accessReview.period.queryKey({
                    periodKey: cycle.periodKey,
                  }),
                })
                await navigate({
                  to: '/workspaces/$workspaceId/reviews/access/period/$periodKey',
                  params: { workspaceId, periodKey: cycle.periodKey },
                })
              }
            }}
          />
          {cycle.status !== 'COMPLETED' ? (
            <WizardNav onBack={() => setViewedStep(3)} />
          ) : null}
        </>
      ) : null}

      {reopenOpen ? (
        <ConfirmDialog
          open
          onOpenChange={(open) => {
            if (!open) setReopenOpen(false)
          }}
          title='Reopen earlier steps?'
          desc="Edit collected accounts or role mappings. Only decisions affected by your changes will return to pending."
          confirmText='Edit earlier steps'
          destructive
          isLoading={reopenMutation.isPending}
          handleConfirm={() => reopenMutation.mutate({ cycleId })}
        />
      ) : null}

      {importMode && !readOnly && !entitlementsConfirmed ? (
        <ImportCustomAppUsersDialog
          open
          onOpenChange={(open) => {
            if (!open) {
              setImportMode(null)
              void invalidate()
            }
          }}
          cycleId={cycleId}
          customApplicationId={
            cycle.scopeType === 'CUSTOM' ? (cycle.scopeId ?? undefined) : undefined
          }
          applicationName={applicationName}
          initialMode={importMode}
        />
      ) : null}
    </div>
  )
}

function EntitlementConfirmation({
  entitlements,
  confirmedAt,
  readOnly,
  saving,
  members,
  onConfirm,
}: {
  entitlements: AccessReviewEntitlementDTO[]
  confirmedAt: string | null
  readOnly: boolean
  saving: boolean
  members: UserSelectMember[]
  onConfirm: (
    entitlements: Array<{
      key: string
      privilegeTier: AccessReviewPrivilegeTier
      assignedReviewerUserId: string | null
    }>
  ) => void
}) {
  const [drafts, setDrafts] = useState(() =>
    Object.fromEntries(
      entitlements.map((entitlement) => [
        entitlement.key,
        {
          privilegeTier: entitlement.privilegeTier,
          assignedReviewerUserId: entitlement.assignedReviewerUserId ?? '',
        },
      ])
    )
  )
  const locked = readOnly || Boolean(confirmedAt)
  const updateDraft = (
    key: string,
    update: Partial<{
      privilegeTier: AccessReviewPrivilegeTier
      assignedReviewerUserId: string
    }>
  ) =>
    setDrafts((current) => ({
      ...current,
      [key]: { ...current[key]!, ...update },
    }))

  return (
    <section className='border-border mb-4 overflow-hidden rounded-md border bg-card'>
      <div className='border-border flex items-center justify-between gap-3 border-b px-4 py-3'>
        <div>
          <h2 className='text-text-secondary text-[12px] font-semibold'>
            Roles &amp; privilege tiers
          </h2>
          <p className='text-text-subtle mt-0.5 text-[10px]'>
            {entitlements.length} entitlement
            {entitlements.length === 1 ? '' : 's'} · one reviewer may be routed
            per entitlement
          </p>
        </div>
        {confirmedAt ? (
          <span className='rounded-full bg-status-green-surface-subtle px-2 py-1 text-[9px] font-semibold text-status-green-deep'>
            Confirmed
          </span>
        ) : null}
      </div>
      <div className='divide-border divide-y'>
        {entitlements.map((entitlement) => {
          const draft = drafts[entitlement.key]!
          return (
            <div
              key={entitlement.key}
              className='grid gap-3 px-4 py-3 md:grid-cols-[minmax(180px,1fr)_140px_minmax(180px,1fr)] md:items-center'
            >
              <div>
                <p className='text-text-secondary text-[12px] font-medium'>
                  {entitlement.name}
                </p>
                <p className='text-text-subtle mt-0.5 text-[10px]'>
                  {entitlement.grantCount} grant
                  {entitlement.grantCount === 1 ? '' : 's'} · tier via{' '}
                  {entitlement.privilegeTierSource
                    .toLowerCase()
                    .replaceAll('_', ' ')}
                </p>
              </div>
              <div className='text-text-subtle text-[9px] font-medium uppercase'>
                Privilege tier
                <Select
                  value={draft.privilegeTier}
                  disabled={locked || saving}
                  onValueChange={(value) =>
                    updateDraft(entitlement.key, {
                      privilegeTier: value as AccessReviewPrivilegeTier,
                    })
                  }
                >
                  <SelectTrigger className='mt-1 h-8 w-full text-[11px] normal-case'>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(['ADMIN', 'USER', 'VIEWER', 'UNKNOWN'] as const).map(
                      (tier) => (
                        <SelectItem key={tier} value={tier}>
                          {TIER_LABEL[tier]}
                        </SelectItem>
                      )
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div className='text-text-subtle text-[9px] font-medium uppercase'>
                Reviewer
                <div className='mt-1 flex items-center gap-2 normal-case'>
                  <UserSelect
                    value={draft.assignedReviewerUserId}
                    onValueChange={(value) =>
                      updateDraft(entitlement.key, {
                        assignedReviewerUserId: value,
                      })
                    }
                    members={members}
                    placeholder='Application reviewer'
                    disabled={locked || saving}
                    className='h-8 text-[11px]'
                  />
                  {draft.assignedReviewerUserId && !locked ? (
                    <Button
                      size='sm'
                      variant='ghost'
                      className='text-text-subtle shrink-0 px-2 text-[10px]'
                      disabled={saving}
                      onClick={() =>
                        updateDraft(entitlement.key, {
                          assignedReviewerUserId: '',
                        })
                      }
                    >
                      Use application reviewer
                    </Button>
                  ) : null}
                </div>
              </div>
            </div>
          )
        })}
      </div>
      {!locked ? (
        <div className='border-border border-t px-4 py-3'>
          <Button
            size='sm'
            variant='outline'
            disabled={saving}
            onClick={() =>
              onConfirm(
                entitlements.map((entitlement) => ({
                  key: entitlement.key,
                  privilegeTier: drafts[entitlement.key]!.privilegeTier,
                  assignedReviewerUserId:
                    drafts[entitlement.key]!.assignedReviewerUserId || null,
                }))
              )
            }
          >
            {saving ? 'Confirming…' : 'Confirm mapping'}
          </Button>
        </div>
      ) : null}
    </section>
  )
}

function AccountReview({
  items,
  readOnly,
  updating,
  onDecision,
  onBulkDecision,
  onCertifyRoutine,
  onRefresh,
}: {
  items: AccessReviewItemDTO[]
  readOnly: boolean
  updating: boolean
  onDecision: (itemId: string, decision: ItemDecision, notes?: string) => void
  onBulkDecision: (
    itemIds: string[],
    decision: ItemDecision,
    notes?: string
  ) => Promise<void>
  onCertifyRoutine: (items: AccessReviewItemDTO[]) => void
  onRefresh: () => Promise<void>
}) {
  const [activeFilter, setActiveFilter] = useState<
    'needs-attention' | 'flagged' | 'routine' | 'denied' | null
  >(null)
  const [entitlementFilter, setEntitlementFilter] = useState('all')
  const [search, setSearch] = useState('')
  // Work the remaining grants by default; once everything is decided, show
  // all so the reviewer can still change a decision.
  const [statusFilter, setStatusFilter] = useState<
    'remaining' | 'decided' | 'all'
  >(() =>
    items.some((item) => item.decision === 'PENDING_DECISION')
      ? 'remaining'
      : 'all'
  )
  const [typeFilter, setTypeFilter] = useState<'all' | AccountKind>('all')
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [bulkReason, setBulkReason] = useState<'REVOKE' | null>(null)
  const [bulkClassifyOpen, setBulkClassifyOpen] = useState(false)
  const entitlementOptions = useMemo(() => {
    const options = new Map<string, string>()
    for (const item of items) {
      const key = item.entitlementExternalId ?? `name:${item.entitlementName}`
      options.set(key, item.entitlementName ?? 'Application access')
    }
    return [...options.entries()]
  }, [items])
  const visibleItems = useMemo(() => {
    const needle = search.trim().toLowerCase()
    return items.filter(
      (item) =>
        (entitlementFilter === 'all' ||
          (item.entitlementExternalId ?? `name:${item.entitlementName}`) ===
            entitlementFilter) &&
        (typeFilter === 'all' || accountKind(item) === typeFilter) &&
        (!needle ||
          [
            item.principalDisplayName,
            item.principalEmail,
            item.principalExternalId,
          ].some((value) => value?.toLowerCase().includes(needle)))
    )
  }, [entitlementFilter, items, search, typeFilter])
  const groupedItems = useMemo(() => {
    const routine: AccessReviewItemDTO[] = []
    const exceptions: AccessReviewItemDTO[] = []
    for (const item of visibleItems) {
      if (isRoutine(item)) routine.push(item)
      else exceptions.push(item)
    }
    return { routine, exceptions }
  }, [visibleItems])
  const pendingRoutine = groupedItems.routine.filter(
    (item) => item.decision === 'PENDING_DECISION'
  )
  const applicationBlockingExceptions = items.filter(
    (item) =>
      item.decision === 'PENDING_DECISION' &&
      (effectivePriority(item) === 'P0' ||
        effectivePriority(item) === 'P1' ||
        item.privilegeTier === 'ADMIN' ||
        PRIVILEGED_RULES.has(item.ruleMatched ?? ''))
  )
  const needsAttentionItems = groupedItems.exceptions.filter(
    (item) =>
      effectivePriority(item) === 'P0' || effectivePriority(item) === 'P1'
  )
  const flaggedItems = groupedItems.exceptions.filter(
    (item) => effectivePriority(item) === 'P2'
  )
  const deniedItems = visibleItems.filter(
    (item) => item.decision === 'REVOKE'
  )
  const cardItems =
    activeFilter === 'needs-attention'
      ? needsAttentionItems
      : activeFilter === 'flagged'
        ? flaggedItems
        : activeFilter === 'denied'
          ? deniedItems
          : activeFilter === 'routine'
            ? groupedItems.routine
            : groupedItems.exceptions
  const remainingCount = cardItems.filter(
    (item) => item.decision === 'PENDING_DECISION'
  ).length
  const tableItems =
    statusFilter === 'remaining'
      ? cardItems.filter((item) => item.decision === 'PENDING_DECISION')
      : statusFilter === 'decided'
        ? cardItems.filter((item) => item.decision !== 'PENDING_DECISION')
        : cardItems
  const tableHeading =
    activeFilter === 'needs-attention'
      ? 'Needs attention (P0/P1)'
      : activeFilter === 'flagged'
        ? 'Flagged (P2)'
        : activeFilter === 'denied'
          ? 'Denied: desired-state removal'
          : activeFilter === 'routine'
            ? 'Routine: unchanged since prior review, recent activity'
            : 'Exceptions & changes'
  const toggleFilter = (
    filter: 'needs-attention' | 'flagged' | 'routine' | 'denied'
  ) => setActiveFilter((current) => (current === filter ? null : filter))

  // Selection persists across filters and after bulk updates; only Clear
  // empties it. Bulk actions apply to the selected rows currently shown.
  const selectedVisible = tableItems.filter((item) => selectedIds.has(item.id))
  const allVisibleSelected =
    tableItems.length > 0 && selectedVisible.length === tableItems.length
  const toggleSelectAll = () =>
    setSelectedIds((current) => {
      const next = new Set(current)
      if (allVisibleSelected) tableItems.forEach((item) => next.delete(item.id))
      else tableItems.forEach((item) => next.add(item.id))
      return next
    })
  const toggleSelect = (id: string) =>
    setSelectedIds((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  // Rows the registry can key: an identity resolved to a provider principal.
  const bulkClassifiable = selectedVisible.filter(
    (item) => !!item.principalExternalId
  )
  const startBulk = (decision: BulkAction) => {
    if (selectedVisible.length === 0) return
    if (decision === 'MARK_SERVICE_ACCOUNT') {
      if (bulkClassifiable.length > 0) setBulkClassifyOpen(true)
      return
    }
    // Deny needs a reason; certify never does — the same rules as a single
    // decision.
    if (decision === 'CERTIFY') {
      void onBulkDecision(
        selectedVisible.map((item) => item.id),
        'CERTIFY'
      )
      return
    }
    setBulkReason(decision)
  }

  return (
    <div className='space-y-4'>
      <MarkServiceAccountDialog
        open={bulkClassifyOpen}
        onOpenChange={(open) => {
          setBulkClassifyOpen(open)
          if (!open) void onRefresh()
        }}
        principals={bulkClassifiable.map((item) => ({
          systemName: item.systemName,
          principalExternalId: item.principalExternalId!,
          principalDisplayName: item.principalDisplayName,
          principalEmail: item.principalEmail,
        }))}
      />
      <div className='grid grid-cols-2 gap-2 md:grid-cols-4'>
        <ReviewStat
          label='Needs attention'
          value={needsAttentionItems.length}
          qualifier='P0/P1'
          selected={activeFilter === 'needs-attention'}
          onSelect={() => toggleFilter('needs-attention')}
        />
        <ReviewStat
          label='Flagged'
          value={flaggedItems.length}
          qualifier='P2'
          selected={activeFilter === 'flagged'}
          onSelect={() => toggleFilter('flagged')}
        />
        <ReviewStat
          label='Routine'
          value={groupedItems.routine.length}
          selected={activeFilter === 'routine'}
          onSelect={() => toggleFilter('routine')}
        />
        <ReviewStat
          label='Denied'
          value={deniedItems.length}
          selected={activeFilter === 'denied'}
          onSelect={() => toggleFilter('denied')}
        />
      </div>

      <div className='flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between'>
        <p className='text-text-subtle text-[10px] leading-4'>
          Needs attention and Flagged are RFC priority buckets (P0/P1 and P2).
          Routine means unchanged with recent activity and no risk signal.
          Denied is a decision outcome, so it can overlap another card. Select a
          card to filter the records below.
        </p>
        {activeFilter ? (
          <Button
            size='sm'
            variant='link'
            className='h-auto shrink-0 p-0 text-[10px]'
            onClick={() => setActiveFilter(null)}
          >
            Clear filter
          </Button>
        ) : null}
      </div>

      <section className='border-border overflow-hidden rounded-md border bg-card'>
        <div className='border-border flex flex-col gap-3 border-b px-4 py-3 lg:flex-row lg:items-center lg:justify-between'>
          <div>
            <h2 className='text-text-secondary text-[12px] font-semibold'>
              {tableHeading}
            </h2>
            <p className='text-text-subtle mt-0.5 text-[10px]'>
              {tableItems.length} grant{tableItems.length === 1 ? '' : 's'}
              {activeFilter === null
                ? ` · ${groupedItems.routine.length} routine grant${groupedItems.routine.length === 1 ? '' : 's'} under the Routine card`
                : ''}
            </p>
            <ToggleGroup
              type='single'
              value={statusFilter}
              onValueChange={(value) =>
                value && setStatusFilter(value as typeof statusFilter)
              }
              aria-label='Show grants by decision status'
              className='mt-2 justify-start'
            >
              <ToggleGroupItem
                value='remaining'
                className='h-7 px-2.5 text-[11px]'
              >
                Remaining ({remainingCount})
              </ToggleGroupItem>
              <ToggleGroupItem value='decided' className='h-7 px-2.5 text-[11px]'>
                Decided ({cardItems.length - remainingCount})
              </ToggleGroupItem>
              <ToggleGroupItem value='all' className='h-7 px-2.5 text-[11px]'>
                All
              </ToggleGroupItem>
            </ToggleGroup>
          </div>
          <div className='flex flex-wrap items-center gap-2'>
            <div className='relative'>
              <Search className='text-text-placeholder pointer-events-none absolute top-1/2 left-2.5 h-3.5 w-3.5 -translate-y-1/2' />
              <Input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder='Search name or email'
                aria-label='Search accounts'
                className='h-8 w-[200px] pl-8 text-[11px]'
              />
            </div>
            {entitlementOptions.length > 1 ? (
              <Select
                value={entitlementFilter}
                onValueChange={(value) => {
                  setEntitlementFilter(value)
                  setActiveFilter(null)
                }}
              >
                <SelectTrigger
                  className='h-8 w-[170px] text-[11px]'
                  aria-label='Filter by role'
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='all'>All roles</SelectItem>
                  {entitlementOptions.map(([key, name]) => (
                    <SelectItem key={key} value={key}>
                      {name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : null}
            <Select
              value={typeFilter}
              onValueChange={(value) => setTypeFilter(value as typeof typeFilter)}
            >
              <SelectTrigger
                className='h-8 w-[150px] text-[11px]'
                aria-label='Filter by account type'
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='all'>All account types</SelectItem>
                {(Object.keys(ACCOUNT_KIND_LABEL) as AccountKind[]).map(
                  (kind) => (
                    <SelectItem key={kind} value={kind}>
                      {ACCOUNT_KIND_LABEL[kind]}
                    </SelectItem>
                  )
                )}
              </SelectContent>
            </Select>
          </div>
          {!readOnly && pendingRoutine.length > 0 ? (
            <Button
              size='sm'
              variant='outline'
              disabled={updating || applicationBlockingExceptions.length > 0}
              title={
                applicationBlockingExceptions.length
                  ? `Decide ${applicationBlockingExceptions.length} P0/P1 or privileged grant${applicationBlockingExceptions.length === 1 ? '' : 's'} first.`
                  : undefined
              }
              onClick={() => onCertifyRoutine(pendingRoutine)}
            >
              Certify all {pendingRoutine.length} routine
            </Button>
          ) : null}
        </div>
        <Table>
          <TableHeader>
            {/* Multi-select: when rows are selected, the actionable header
                swaps to compact bulk controls — no separate action bar. */}
            <TableRow
              className={`h-[40px] border-b hover:bg-transparent ${
                selectedVisible.length > 0 ? 'bg-selected' : 'bg-surface-subtle'
              }`}
            >
              <TableHead className='w-[36px] pr-2 pl-4'>
                {!readOnly ? (
                  <Checkbox
                    checked={allVisibleSelected}
                    onCheckedChange={toggleSelectAll}
                    className='h-3.5 w-3.5 rounded-[3px]'
                    aria-label='Select all grants'
                  />
                ) : null}
              </TableHead>
              <TableHead>
                {selectedVisible.length > 0 ? (
                  <span className='flex items-center gap-2'>
                    <span className='text-[11px] font-semibold text-cobalt-strong'>
                      {selectedVisible.length} selected{updating ? '…' : ''}
                    </span>
                    <Button
                      size='sm'
                      variant='link'
                      className='text-text-subtle h-auto p-0 text-[11px]'
                      onClick={() => setSelectedIds(new Set())}
                    >
                      Clear
                    </Button>
                    {/* Owner assignment sits here, beside the count, not only
                        in the far-right decision menu: it is the bulk action a
                        reviewer reaches for on a batch of non-human accounts. */}
                    {!readOnly && bulkClassifiable.length > 0 ? (
                      <Button
                        size='sm'
                        variant='outline'
                        className='ml-2 h-6 rounded-sm border-cobalt-border bg-surface px-2 text-[11px]'
                        disabled={updating}
                        onClick={() => startBulk('MARK_SERVICE_ACCOUNT')}
                      >
                        Mark as service account · assign owner…
                      </Button>
                    ) : null}
                  </span>
                ) : (
                  'Account'
                )}
              </TableHead>
              <TableHead>{selectedVisible.length > 0 ? '' : 'Type'}</TableHead>
              <TableHead>{selectedVisible.length > 0 ? '' : 'Owner'}</TableHead>
              <TableHead>{selectedVisible.length > 0 ? '' : 'Role'}</TableHead>
              <TableHead>{selectedVisible.length > 0 ? '' : 'Insight'}</TableHead>
              <TableHead className='sticky right-0 bg-inherit pr-4 text-right'>
                {selectedVisible.length > 0 ? (
                  <Select
                    value=''
                    onValueChange={(value) =>
                      startBulk(value as BulkAction)
                    }
                    disabled={updating}
                  >
                    <SelectTrigger className='ml-auto h-6 w-[150px] rounded-sm border-cobalt-border bg-card text-[10px]'>
                      <SelectValue placeholder='Decide selected' />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='CERTIFY'>Certify selected</SelectItem>
                      <SelectItem value='REVOKE'>Deny selected…</SelectItem>
                      <SelectItem
                        value='MARK_SERVICE_ACCOUNT'
                        disabled={bulkClassifiable.length === 0}
                      >
                        Mark as service account…
                      </SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  'Decision'
                )}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tableItems.length ? (
              tableItems.map((item) => (
                <AccountRow
                  key={item.id}
                  item={item}
                  readOnly={readOnly}
                  disabled={updating}
                  selected={selectedIds.has(item.id)}
                  onToggleSelect={() => toggleSelect(item.id)}
                  onDecision={(decision, notes) =>
                    onDecision(item.id, decision, notes)
                  }
                  onRefresh={onRefresh}
                />
              ))
            ) : (
              <TableRow className='hover:bg-transparent'>
                <TableCell
                  colSpan={7}
                  className='text-text-subtle py-6 text-center text-[11px]'
                >
                  {statusFilter === 'remaining' && cardItems.length > 0 ? (
                    <span className='inline-flex items-center gap-1.5'>
                      <CheckCircle2 className='text-status-green h-4 w-4' />
                      Every grant here has a decision. Switch to Decided or
                      All to change one.
                    </span>
                  ) : activeFilter ? (
                    'No grants match this filter.'
                  ) : (
                    'No exceptions or changes require individual review.'
                  )}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </section>

      {bulkReason ? (
        <ReasonDialog
          count={selectedVisible.length}
          onClose={() => setBulkReason(null)}
          onConfirm={(notes) => {
            const targets = selectedVisible.map((item) => item.id)
            setBulkReason(null)
            void onBulkDecision(targets, bulkReason, notes)
          }}
        />
      ) : null}
    </div>
  )
}

function AccountRow({
  item,
  readOnly,
  disabled,
  selected,
  onToggleSelect,
  onDecision,
  onRefresh,
}: {
  item: AccessReviewItemDTO
  readOnly: boolean
  disabled: boolean
  selected: boolean
  onToggleSelect: () => void
  onDecision: (decision: ItemDecision, notes?: string) => void
  onRefresh: () => Promise<void>
}) {
  const [reasonDecision, setReasonDecision] = useState<
    'REVOKE' | null
  >(null)
  const [historyOpen, setHistoryOpen] = useState(false)
  // A decided row shows its decision; "Change" reopens the buttons.
  const [changing, setChanging] = useState(false)
  const decided = item.decision !== 'PENDING_DECISION' && !changing
  const [identityOpen, setIdentityOpen] = useState(false)
  const [classificationOpen, setClassificationOpen] = useState(false)
  const peopleTestAccount =
    item.employee?.complianceExclusionReason === 'TEST_ACCOUNT'
  const unknown =
    (item.principalType === 'UNKNOWN' || item.principalType === 'EMPLOYEE') &&
    !!item.principalEmail &&
    !item.serviceAccount
  const classifiable =
    !peopleTestAccount &&
    (item.principalType === 'SERVICE_ACCOUNT' || !!item.serviceAccount) &&
    !!item.principalExternalId
  const name =
    item.principalDisplayName ??
    item.principalEmail ??
    item.principalExternalId ??
    'Unknown account'
  const reason =
    ruleLabel(item.ruleMatched) ??
    changeLabel(item.detectedChangeSinceLastCycle) ??
    item.recommendationReason ??
    'Review individually'
  const kind = accountKind(item)
  const decide = (decision: ItemDecision) => {
    if (decision === 'REVOKE') {
      setReasonDecision(decision)
      return
    }
    setChanging(false)
    onDecision(decision)
  }
  const rowClass = selected
    ? 'bg-selected hover:bg-selected'
    : 'bg-card hover:bg-surface-subtle'

  return (
    <>
      <TableRow
        className={`border-b align-top ${rowClass} ${decided ? 'opacity-60 hover:opacity-100' : ''}`}
      >
        <TableCell className='w-[36px] pt-3.5 pr-2 pl-4'>
          {!readOnly ? (
            <Checkbox
              checked={selected}
              onCheckedChange={onToggleSelect}
              className='h-3.5 w-3.5 rounded-[3px]'
              aria-label={`Select ${name}`}
            />
          ) : null}
        </TableCell>
        <TableCell>
          <div className='flex items-center gap-2'>
            <MemberAvatar
              person={{
                name,
                email: item.principalEmail ?? null,
                isServiceAccount: kind === 'SERVICE',
              }}
              size='sm'
            />
            <div className='max-w-[260px] min-w-0'>
              <span className='text-text-secondary block truncate text-[13px] font-medium'>
                {name}
              </span>
              {item.principalEmail && item.principalEmail !== name ? (
                <span className='text-text-subtle block truncate text-[11px]'>
                  {item.principalEmail}
                </span>
              ) : null}
            </div>
          </div>
          {(unknown || classifiable) && !readOnly ? (
            <div className='mt-1 flex flex-wrap gap-2 pl-8'>
              {unknown ? (
                <Button
                  size='sm'
                  variant='link'
                  className='h-auto p-0 text-[11px]'
                  onClick={() => setIdentityOpen(true)}
                >
                  Correct account owner
                </Button>
              ) : null}
              {classifiable ? (
                <Button
                  size='sm'
                  variant='link'
                  className='h-auto p-0 text-[11px]'
                  onClick={() => setClassificationOpen(true)}
                >
                  Edit classification
                </Button>
              ) : null}
            </div>
          ) : null}
        </TableCell>
        <TableCell>
          <Badge
            className={`border px-2 py-0.5 text-[11px] font-medium ${ACCOUNT_KIND_BADGE_CLASS[kind]}`}
          >
            {ACCOUNT_KIND_LABEL[kind]}
          </Badge>
        </TableCell>
        <TableCell className='min-w-[150px] text-[12px]'>
          {/* Non-person identities carry the accountable human; auditors
              sample this field, so its absence is worth showing too. */}
          {item.serviceAccount ? (
            <>
              <div className='text-text-secondary font-medium'>
                {item.serviceAccount.ownerName}
              </div>
              <p
                className='text-text-subtle mt-0.5 line-clamp-1 max-w-[220px] text-[11px]'
                title={item.serviceAccount.purpose}
              >
                {item.serviceAccount.purpose}
              </p>
            </>
          ) : kind === 'SERVICE' || kind === 'TEST' ? (
            <span className='text-[11px] font-medium text-status-amber-ink'>
              No owner yet
            </span>
          ) : (
            <span className='text-text-subtle'>—</span>
          )}
        </TableCell>
        <TableCell className='text-text-body min-w-[140px] text-[12px] font-medium'>
          <div>{item.entitlementName ?? accessLevelLabel(item.accessLevel)}</div>
          <div className='mt-1'>
            {item.privilegeTier ? (
              <PrivilegeTierBadge tier={item.privilegeTier} />
            ) : (
              <span className='text-text-subtle text-[11px]'>Unmapped</span>
            )}
          </div>
        </TableCell>
        <TableCell className='text-text-body text-[11px]'>
          <div className='flex w-[240px] flex-wrap items-center gap-x-1.5 gap-y-1'>
            {item.ruleMatched ? (
              <span className='text-text-secondary font-semibold'>
                {item.ruleMatched.replaceAll('_', ' ')}
              </span>
            ) : null}
            {item.recommendation ? (
              <>
                <span className='text-text-subtle'>→</span>
                <Badge
                  status={recommendationStatus(item.recommendation)}
                  className='px-1.5 py-0 text-[10px]'
                >
                  Suggested {recommendationLabel(item.recommendation)}
                </Badge>
              </>
            ) : null}
          </div>
          <p
            className='text-text-subtle mt-0.5 line-clamp-2 w-[240px] leading-5'
            title={item.recommendationReason ?? reason}
          >
            {item.recommendationReason ?? reason}
          </p>
          {item.lastActiveAt ? (
            <div className='text-text-subtle mt-1 flex flex-wrap items-center gap-2'>
              <LastActive lastActiveAt={item.lastActiveAt ?? null} />
            </div>
          ) : null}
        </TableCell>
        <TableCell className='sticky right-0 bg-inherit pr-4 text-right whitespace-nowrap'>
          <div className='flex items-center justify-end gap-1'>
            {decided ? (
              <>
                <span className='flex flex-col items-end'>
                  <DecisionBadge decision={item.decision} />
                  {item.reviewedAt ? (
                    <span className='text-text-subtle mt-0.5 text-[10px]'>
                      {format(new Date(item.reviewedAt), 'MMM d, HH:mm')}
                    </span>
                  ) : null}
                </span>
                {!readOnly ? (
                  <>
                  <Button
                    size='sm'
                    variant='ghost'
                    className='h-8 px-2.5 text-[11px]'
                    disabled={disabled}
                    onClick={() => setChanging(true)}
                  >
                    Change
                  </Button>
                  <Button size='sm' variant='ghost' disabled={disabled} onClick={() => onDecision('PENDING_DECISION')}>Withdraw</Button>
                  </>
                ) : null}
              </>
            ) : (
              <>
                {/* While changing, the current decision stays filled so
                    "Keep" is unambiguous. */}
                <Button
                  size='sm'
                  variant={item.decision === 'CERTIFY' ? 'default' : 'outline'}
                  className='h-8 rounded-full px-3 text-[11px]'
                  disabled={disabled || readOnly}
                  onClick={() => decide('CERTIFY')}
                >
                  Certify
                </Button>
                <Button
                  size='sm'
                  variant={item.decision === 'REVOKE' ? 'destructive' : 'outline'}
                  className={`h-8 rounded-full px-3 text-[11px] ${
                    item.decision === 'REVOKE' ? '' : 'text-destructive'
                  }`}
                  disabled={disabled || readOnly}
                  onClick={() => decide('REVOKE')}
                >
                  Deny
                </Button>
                {changing ? (
                  <Button
                    size='sm'
                    variant='ghost'
                    className='h-7 px-2 text-[10px]'
                    onClick={() => setChanging(false)}
                  >
                    Keep
                  </Button>
                ) : null}
              </>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  size='icon'
                  variant='ghost'
                  className='h-7 w-7'
                  aria-label={`More actions for ${name}`}
                >
                  <MoreHorizontal className='h-3.5 w-3.5' />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align='end'>
                <DropdownMenuItem
                  onClick={() => setHistoryOpen((open) => !open)}
                >
                  View decision history
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </TableCell>
      </TableRow>
      {historyOpen ? (
        <TableRow className='bg-surface-subtle hover:bg-surface-subtle'>
          <TableCell colSpan={7} className='px-4 py-3'>
            <DecisionHistory itemId={item.id} />
          </TableCell>
        </TableRow>
      ) : null}
      {unknown ? (
        <AddUnknownPrincipalDialog
          open={identityOpen}
          itemId={item.id}
          onOpenChange={(open) => {
            setIdentityOpen(open)
            if (!open) void onRefresh()
          }}
          prefillEmail={item.principalEmail ?? ''}
          prefillDisplayName={item.principalDisplayName}
          onClassifyNonHuman={
            item.principalExternalId
              ? () => setClassificationOpen(true)
              : undefined
          }
          readOnly={readOnly}
        />
      ) : null}
      {unknown || classifiable ? (
        <MarkServiceAccountDialog
          open={classificationOpen}
          onOpenChange={(open) => {
            setClassificationOpen(open)
            if (!open) void onRefresh()
          }}
          systemName={item.systemName}
          principalExternalId={item.principalExternalId!}
          principalDisplayName={item.principalDisplayName}
          principalEmail={item.principalEmail}
          existing={
            item.serviceAccount
              ? {
                  ownerEmployeeId: item.serviceAccount.ownerEmployeeId,
                  purpose: item.serviceAccount.purpose,
                  isTestAccount: item.serviceAccount.isTestAccount,
                }
              : null
          }
        />
      ) : null}
      {reasonDecision ? (
        <ReasonDialog
          onClose={() => setReasonDecision(null)}
          onConfirm={(notes) => {
            setChanging(false)
            onDecision(reasonDecision, notes)
            setReasonDecision(null)
          }}
        />
      ) : null}
    </>
  )
}

function ReasonDialog({
  count = 1,
  onClose,
  onConfirm,
}: {
  /** Bulk decisions share one reason across every selected grant. */
  count?: number
  onClose: () => void
  onConfirm: (notes: string) => void
}) {
  const [notes, setNotes] = useState('')
  const title = 'Deny access'
  const description =
    'Explain why this account should no longer have access. Screenata records the desired state but does not change the application.'
  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4'>
      <div className='border-border w-full max-w-md rounded-md border bg-card p-5'>
        <h3 className='text-text-secondary text-[14px] font-semibold'>
          {title}
          {count > 1 ? ` · ${count} grants` : ''}
        </h3>
        <p className='text-text-body mt-1 text-[11px] leading-5'>
          {description}
          {count > 1 ? ' The same reason is recorded on every selected grant.' : ''}
        </p>
        <label
          htmlFor='access-review-decision-reason'
          className='text-text-secondary mt-3 block text-[11px] font-medium'
        >
          Reason (required)
        </label>
        <textarea
          id='access-review-decision-reason'
          autoFocus
          rows={4}
          maxLength={2000}
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
          placeholder='Add enough context for an auditor to understand this decision.'
          className='border-input focus:ring-ring mt-1 w-full rounded-md border px-3 py-2 text-[12px] outline-none focus:ring-1'
        />
        <div className='mt-4 flex justify-end gap-2'>
          <Button variant='ghost' size='sm' onClick={onClose}>
            Cancel
          </Button>
          <Button
            size='sm'
            disabled={!notes.trim()}
            onClick={() => onConfirm(notes.trim())}
          >
            Record decision
          </Button>
        </div>
      </div>
    </div>
  )
}

/**
 * Complete step — the decision record for this application: what to change
 * in the real application (denied / modified first), what was accepted, and
 * a CSV export of the same rows as the signed pack's decisions.csv.
 */
function DecisionRecord({
  cycleId,
  items,
  reviewerNames,
  canRemediate,
  remediating,
  onRemediate,
}: {
  cycleId: string
  items: AccessReviewItemDTO[]
  reviewerNames: Map<string, string>
  canRemediate: boolean
  remediating: boolean
  onRemediate: (itemId: string, remediated: boolean, notes?: string) => void
}) {
  const [exporting, setExporting] = useState(false)
  const [remediationTarget, setRemediationTarget] =
    useState<AccessReviewItemDTO | null>(null)
  const order: Record<string, number> = {
    REVOKE: 0,
    MODIFY: 1,
    ACCEPT_RISK: 2,
    COMPENSATE: 3,
    CERTIFY: 4,
    PENDING_DECISION: 5,
  }
  const rows = [...items].sort(
    (a, b) =>
      (order[a.decision] ?? 9) - (order[b.decision] ?? 9) ||
      (a.principalDisplayName ?? '').localeCompare(b.principalDisplayName ?? '')
  )
  const count = (decision: string) =>
    items.filter((item) => item.decision === decision).length
  const actionable = count('REVOKE') + count('MODIFY')
  const remediated = items.filter(
    (item) => DENIED_DECISIONS.has(item.decision) && item.remediatedAt
  ).length
  const exportCsv = async () => {
    setExporting(true)
    try {
      const result = await trpcClient.accessReview.decisionsCsv.query({
        cycleId,
      })
      downloadBlob(
        new Blob([result.csv], { type: 'text/csv;charset=utf-8' }),
        result.filename
      )
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Could not export decisions'
      )
    } finally {
      setExporting(false)
    }
  }

  return (
    <section className='border-border overflow-hidden rounded-md border bg-card'>
      <div className='flex flex-wrap items-center justify-between gap-3 px-4 py-3'>
        <div>
          <h2 className='text-text-secondary text-[12px] font-semibold'>
            Decision record
          </h2>
          <p className='text-text-subtle mt-0.5 text-[10px] leading-4'>
            {items.length} grants · {count('CERTIFY')} certified ·{' '}
            {count('REVOKE')} denied
            {actionable > 0
              ? ` · ${remediated} of ${actionable} removed. Apply each change in the application, then record it here.`
              : ''}
          </p>
        </div>
        <Button
          size='sm'
          variant='outline'
          disabled={exporting || items.length === 0}
          onClick={exportCsv}
        >
          <Download className='mr-1.5 h-3.5 w-3.5' />
          {exporting ? 'Exporting…' : 'Export CSV'}
        </Button>
      </div>
      {rows.length > 0 ? (
        <Table>
          <TableHeader>
            <TableRow className='bg-surface-subtle hover:bg-transparent'>
              <TableHead className='pl-4'>Account</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Decision</TableHead>
              <TableHead>Reviewer</TableHead>
              <TableHead>Reason</TableHead>
              <TableHead className='pr-4'>Remediation</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((item) => (
              <TableRow key={item.id} className='border-b align-top'>
                <TableCell className='pl-4'>
                  <span className='text-text-secondary block text-[12px] font-medium'>
                    {item.principalDisplayName ??
                      item.principalEmail ??
                      item.principalExternalId ??
                      'Unknown account'}
                  </span>
                  {item.principalEmail ? (
                    <span className='text-text-subtle block text-[10px]'>
                      {item.principalEmail}
                    </span>
                  ) : null}
                </TableCell>
                <TableCell className='text-text-body text-[11px]'>
                  {item.entitlementName ?? accessLevelLabel(item.accessLevel)}
                  {item.privilegeTier ? (
                    <div className='mt-1'>
                      <PrivilegeTierBadge tier={item.privilegeTier} />
                    </div>
                  ) : null}
                </TableCell>
                <TableCell>
                  <DecisionBadge decision={item.decision} />
                </TableCell>
                <TableCell className='text-text-body text-[11px] whitespace-nowrap'>
                  {item.reviewedBy
                    ? (reviewerNames.get(item.reviewedBy) ?? item.reviewedBy)
                    : '—'}
                  {item.reviewedAt ? (
                    <div className='text-text-subtle text-[10px]'>
                      {format(new Date(item.reviewedAt), 'MMM d, yyyy HH:mm')}
                    </div>
                  ) : null}
                </TableCell>
                <TableCell className='text-text-subtle max-w-[240px] text-[10px] leading-4'>
                  {item.notes ?? '—'}
                </TableCell>
                <TableCell className='pr-4'>
                  <RemediationCell
                    item={item}
                    reviewerNames={reviewerNames}
                    canRemediate={canRemediate}
                    disabled={remediating}
                    onMark={() => setRemediationTarget(item)}
                    onReopen={() => onRemediate(item.id, false)}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <p className='text-text-subtle px-4 pb-4 text-[11px]'>
          No accounts were collected for this application.
        </p>
      )}
      <RecordRemediationDialog
        item={remediationTarget}
        pending={remediating}
        onCancel={() => setRemediationTarget(null)}
        onConfirm={(notes) => {
          if (remediationTarget) {
            onRemediate(remediationTarget.id, true, notes)
          }
          setRemediationTarget(null)
        }}
      />
    </section>
  )
}

/**
 * RFC-227 — the remediation state of one grant. Certified grants needed no
 * change, so they show nothing rather than a misleading empty state.
 */
function RemediationCell({
  item,
  reviewerNames,
  canRemediate,
  disabled,
  onMark,
  onReopen,
}: {
  item: AccessReviewItemDTO
  reviewerNames: Map<string, string>
  canRemediate: boolean
  disabled: boolean
  onMark: () => void
  onReopen: () => void
}) {
  if (!DENIED_DECISIONS.has(item.decision)) {
    return <span className='text-text-subtle text-[10px]'>Not required</span>
  }
  if (item.remediatedAt) {
    return (
      <div className='text-[10px] leading-4'>
        <Badge status='green' className='text-[9px]'>
          Removed
        </Badge>
        <div className='text-text-subtle mt-1'>
          {format(new Date(item.remediatedAt), 'MMM d, yyyy')}
          {item.remediatedBy
            ? ` · ${reviewerNames.get(item.remediatedBy) ?? item.remediatedBy}`
            : ''}
        </div>
        {item.remediationNotes ? (
          <div className='text-text-subtle mt-0.5'>
            {item.remediationNotes}
          </div>
        ) : null}
        {canRemediate ? (
          <button
            type='button'
            disabled={disabled}
            onClick={onReopen}
            className='text-text-subtle mt-1 underline underline-offset-2 disabled:opacity-50'
          >
            Reopen
          </button>
        ) : null}
      </div>
    )
  }
  return (
    <div className='text-[10px] leading-4'>
      <Badge status='amber' className='text-[9px]'>
        Open
      </Badge>
      {canRemediate ? (
        <Button
          size='sm'
          variant='outline'
          disabled={disabled}
          onClick={onMark}
          className='mt-1 h-6 px-2 text-[10px]'
        >
          Mark removed
        </Button>
      ) : null}
    </div>
  )
}

/**
 * Records that a denied grant was actually removed in the application. The
 * note is optional — the date and the person are the parts an auditor reads,
 * and requiring prose here would only produce "done" in every row.
 */
function RecordRemediationDialog({
  item,
  pending,
  onCancel,
  onConfirm,
}: {
  item: AccessReviewItemDTO | null
  pending: boolean
  onCancel: () => void
  onConfirm: (notes?: string) => void
}) {
  const [notes, setNotes] = useState('')
  useEffect(() => {
    if (item) setNotes('')
  }, [item])
  const account =
    item?.principalDisplayName ??
    item?.principalEmail ??
    item?.principalExternalId ??
    'this account'

  return (
    <Dialog open={item !== null} onOpenChange={(open) => !open && onCancel()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Record remediation</DialogTitle>
          <DialogDescription>
            Confirm the denied access for {account} has been removed in the
            application. Screenata does not verify this. It records what you
            report.
          </DialogDescription>
        </DialogHeader>
        <div className='px-6'>
          <Label htmlFor='remediation-notes' className='text-[11px]'>
            What was done (optional)
          </Label>
          <Textarea
            id='remediation-notes'
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            placeholder='e.g. Admin role removed, account suspended'
            className='mt-1.5 text-[12px]'
            rows={3}
          />
        </div>
        <DialogFooter>
          <Button variant='outline' onClick={onCancel} disabled={pending}>
            Cancel
          </Button>
          <Button
            onClick={() => onConfirm(notes.trim() || undefined)}
            disabled={pending}
          >
            {pending ? 'Recording…' : 'Record removal'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function DecisionBadge({ decision }: { decision: string }) {
  const status =
    decision === 'REVOKE'
      ? 'red'
      : decision === 'MODIFY'
        ? 'blue'
        : decision === 'ACCEPT_RISK' || decision === 'COMPENSATE'
          ? 'amber'
          : decision === 'CERTIFY'
            ? 'green'
            : undefined
  return (
    <Badge status={status} className='text-[9px]'>
      {decision === 'PENDING_DECISION'
        ? 'Pending'
        : decision === 'CERTIFY'
          ? 'Certified'
          : decisionLabel(decision)}
    </Badge>
  )
}

function CompleteApplication({
  cycleId,
  status,
  periodSigned,
  grantCount,
  pendingCount,
  canWrite,
  onCompleted,
}: {
  cycleId: string
  status: string
  periodSigned: boolean
  grantCount: number
  pendingCount: number
  canWrite: boolean
  onCompleted: () => Promise<void>
}) {
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [coverageNote, setCoverageNote] = useState('')
  const mutation = useMutation({
    ...trpc.accessReview.completeCycle.mutationOptions(),
    onError: (error: Error) => toast.error(error.message),
    onSuccess: async () => {
      setConfirmOpen(false)
      toast.success('Application review completed')
      await onCompleted()
    },
  })
  if (status === 'COMPLETED') {
    return (
      <div className='mt-6 rounded-md border border-status-green-border-subtle bg-status-green-surface-subtle px-4 py-3 text-[11px] text-status-green-deep'>
        <CheckCircle2 className='mr-1.5 inline h-3.5 w-3.5' />
        {periodSigned
          ? 'This review is signed. The application and its evidence are read-only.'
          : 'Application complete. To make changes, use Reopen for editing in the review’s application list.'}
      </div>
    )
  }
  if (!canWrite) return null
  const populationDisclosed = grantCount > 0 || coverageNote.trim().length > 0
  const canComplete = pendingCount === 0 && populationDisclosed
  return (
    <section className='border-border mt-6 rounded-md border bg-card px-4 py-4'>
      <div className='flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between'>
        <div>
          <h2 className='text-text-secondary text-[12px] font-semibold'>
            Complete application
          </h2>
          <p className='text-text-subtle mt-1 text-[10px] leading-4'>
            {pendingCount > 0
              ? `Decide ${pendingCount} remaining grant${pendingCount === 1 ? '' : 's'} first.`
              : 'Completing freezes this application pack. The review is completed separately once every application is done.'}
          </p>
        </div>
        <Button
          size='sm'
          disabled={!canComplete || mutation.isPending}
          onClick={() => setConfirmOpen(true)}
        >
          Complete application
        </Button>
      </div>
      {grantCount === 0 ? (
        <div className='mt-3'>
          <label className='text-text-secondary text-[10px] font-medium'>
            Empty population explanation (required)
          </label>
          <textarea
            rows={2}
            value={coverageNote}
            onChange={(event) => setCoverageNote(event.target.value)}
            className='border-border mt-1 w-full rounded-md border px-3 py-2 text-[11px] outline-none focus:border-ring'
          />
        </div>
      ) : null}
      {confirmOpen ? (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4'>
          <div className='border-border w-full max-w-md rounded-md border bg-card p-5'>
            <h3 className='text-text-secondary text-[14px] font-semibold'>
              Complete this application review?
            </h3>
            <p className='text-text-body mt-2 text-[11px] leading-5'>
              Decisions become read-only and the signed application pack is
              frozen. This does not complete the review or approve the Test.
            </p>
            <div className='mt-4 flex justify-end gap-2'>
              <Button
                variant='ghost'
                size='sm'
                onClick={() => setConfirmOpen(false)}
              >
                Cancel
              </Button>
              <Button
                size='sm'
                disabled={mutation.isPending}
                onClick={() =>
                  mutation.mutate({
                    cycleId,
                    attestationExceptions: coverageNote.trim() || undefined,
                  })
                }
              >
                {mutation.isPending ? 'Completing…' : 'Complete application'}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  )
}

const TIER_LABEL: Record<AccessReviewPrivilegeTier, string> = {
  ADMIN: 'Admin',
  USER: 'User',
  VIEWER: 'Viewer (read-only)',
  UNKNOWN: 'Unknown',
}

const POPULATION_METHOD_LABEL: Record<
  NonNullable<AccessReviewDTO['populationMethod']>,
  string
> = {
  SYNC: 'synced from the integration',
  IMPORT: 'imported list',
  SCREENSHOT: 'screenshot',
}

/** Back / Next row under each wizard step. */
function WizardNav({
  onBack,
  onNext,
  nextLabel,
  nextHint,
}: {
  onBack?: () => void
  onNext?: () => void
  nextLabel?: string
  nextHint?: string
}) {
  return (
    <div className='mt-4 flex items-center justify-between gap-3'>
      <div>
        {onBack ? (
          <Button size='sm' variant='ghost' onClick={onBack}>
            <ArrowLeft className='mr-1.5 h-3.5 w-3.5' />
            Back
          </Button>
        ) : null}
      </div>
      <div className='flex items-center gap-3'>
        {nextHint ? (
          <span className='text-text-subtle text-[11px]'>{nextHint}</span>
        ) : null}
        {nextLabel ? (
          <Button size='sm' disabled={!onNext} onClick={onNext}>
            {nextLabel}
            <ArrowRight className='ml-1.5 h-3.5 w-3.5' />
          </Button>
        ) : null}
      </div>
    </div>
  )
}

/**
 * Collected accounts, with the option to collect again — replacing the
 * snapshot — until the role mapping is confirmed (nothing is decided before
 * that, so the rows are disposable).
 */
function PopulationSummary({
  accountCount,
  method,
  collectedAt,
  recollectMethods,
  syncing,
  onSync,
  onImport,
}: {
  accountCount: number
  method: AccessReviewDTO['populationMethod'] | null
  collectedAt: string | null
  recollectMethods: NonNullable<AccessReviewDTO['populationMethods']>
  syncing: boolean
  onSync: () => void
  onImport: (mode: ImportMode) => void
}) {
  return (
    <section className='border-border flex flex-wrap items-center justify-between gap-3 rounded-md border bg-card px-4 py-3'>
      <div>
        <p className='text-text-secondary text-[12px]'>
          {accountCount} account{accountCount === 1 ? '' : 's'}
          {method ? ` · ${POPULATION_METHOD_LABEL[method]}` : ''}
          {collectedAt ? ` · ${collectedAt}` : ''}
        </p>
        {recollectMethods.length > 0 ? (
          <p className='text-text-subtle mt-0.5 text-[11px]'>
            Collecting again replaces these accounts. Available until the role
            mapping is confirmed.
          </p>
        ) : null}
      </div>
      {recollectMethods.length > 0 ? (
        <div className='flex flex-wrap gap-2'>
          {recollectMethods.includes('SYNC') ? (
            <Button
              size='sm'
              variant='outline'
              disabled={syncing}
              onClick={onSync}
            >
              <RefreshCw className='mr-1.5 h-3.5 w-3.5' />
              {syncing ? 'Starting…' : 'Sync again'}
            </Button>
          ) : null}
          {recollectMethods.includes('IMPORT') ? (
            <Button size='sm' variant='ghost' onClick={() => onImport('upload')}>
              Import a list
            </Button>
          ) : null}
          {recollectMethods.includes('SCREENSHOT') ? (
            <Button
              size='sm'
              variant='ghost'
              onClick={() => onImport('screenshot')}
            >
              Upload a screenshot
            </Button>
          ) : null}
        </div>
      ) : null}
    </section>
  )
}

/**
 * Step 1 — nothing is collected at launch; the reviewer picks how the
 * accounts get here. Sync is offered only when the integration can provide
 * them; a list or screenshot always works, including as the fallback for a
 * dead integration credential.
 */
function CollectAccounts({
  applicationName,
  methods,
  syncing,
  onSync,
  onImport,
}: {
  applicationName: string
  methods: AccessReviewDTO['populationMethods']
  syncing: boolean
  onSync: () => void
  onImport: (mode: ImportMode) => void
}) {
  const available = methods ?? []
  if (available.length === 0) {
    return (
      <section className='border-border rounded-md border border-dashed bg-card px-5 py-10 text-center'>
        <h2 className='text-text-secondary text-[13px] font-medium'>
          No accounts collected
        </h2>
        <p className='text-text-subtle mx-auto mt-1 max-w-xl text-[11px] leading-5'>
          This review has no accounts to decide on.
        </p>
      </section>
    )
  }
  const options: Array<{
    method: NonNullable<AccessReviewDTO['populationMethods']>[number]
    title: string
    detail: string
    icon: typeof Upload
    action: () => void
    label: string
  }> = [
    {
      method: 'SYNC',
      title: `Sync from ${applicationName}`,
      detail:
        'Pull the current accounts and their roles from the connected integration.',
      icon: RefreshCw,
      action: onSync,
      label: syncing ? 'Starting…' : 'Sync now',
    },
    {
      method: 'IMPORT',
      title: 'Import a list',
      detail:
        'Upload the vendor user export (.csv / .xlsx) or paste CSV text.',
      icon: Upload,
      action: () => onImport('upload'),
      label: 'Import list',
    },
    {
      method: 'SCREENSHOT',
      title: 'Upload a screenshot',
      detail:
        'Include the application and organization name, accounts and permissions, and a visible capture date and time (preferably from the source system). Keep the original image; do not add dates afterward. You confirm the extracted table before import.',
      icon: ImageIcon,
      action: () => onImport('screenshot'),
      label: 'Upload screenshot',
    },
  ]
  return (
    <section className='border-border rounded-md border bg-card px-5 py-5'>
      <p className='text-text-subtle text-[11px] leading-5'>
        Choose how the {applicationName} account list gets here. You can
        collect again until the role mapping is confirmed.
      </p>
      <div className='mt-4 grid gap-3 md:grid-cols-3'>
        {options
          .filter((option) => available.includes(option.method))
          .map((option) => (
            <div
              key={option.method}
              className='border-border flex flex-col rounded-md border px-4 py-4'
            >
              <option.icon className='text-text-subtle h-4 w-4' />
              <div className='text-text-secondary mt-2 text-[12px] font-semibold'>
                {option.title}
              </div>
              <p className='text-text-subtle mt-1 flex-1 text-[11px] leading-5'>
                {option.detail}
              </p>
              <Button
                size='sm'
                variant={option.method === 'SYNC' ? 'default' : 'outline'}
                className='mt-3 w-fit'
                disabled={syncing}
                onClick={option.action}
              >
                {option.label}
              </Button>
            </div>
          ))}
      </div>
    </section>
  )
}

function PopulationFailed({
  applicationName,
  message,
  canRetry,
  retrying,
  onRetry,
  onImport,
}: {
  applicationName: string
  message: string
  canRetry: boolean
  retrying: boolean
  onRetry: () => void
  onImport: () => void
}) {
  return (
    <section className='rounded-md border border-status-red-border-subtle bg-status-red-surface-subtle px-5 py-8 text-center'>
      <AlertTriangle className='mx-auto h-5 w-5 text-status-red-alert' />
      <h2 className='mt-3 text-[13px] font-medium text-status-red-alert'>
        Could not collect {applicationName} accounts
      </h2>
      <p className='text-text-body mx-auto mt-1 max-w-xl text-[11px] leading-5'>
        {message}
      </p>
      {canRetry ? (
        <div className='mt-4 flex flex-wrap justify-center gap-2'>
          <Button
            size='sm'
            variant='outline'
            className='bg-card'
            disabled={retrying}
            onClick={onRetry}
          >
            {retrying ? 'Retrying…' : 'Retry collection'}
          </Button>
          <Button size='sm' variant='ghost' onClick={onImport}>
            Import a list instead
          </Button>
        </div>
      ) : null}
    </section>
  )
}

function PopulationLoading({ applicationName }: { applicationName: string }) {
  return (
    <section
      aria-busy='true'
      aria-live='polite'
      className='border-border rounded-md border border-dashed bg-card px-5 py-10'
    >
      <div className='mx-auto max-w-xl text-center'>
        <LoaderCircle className='mx-auto h-5 w-5 animate-spin text-status-blue' />
        <h2 className='text-text-secondary mt-3 text-[13px] font-medium'>
          Collecting {applicationName} accounts…
        </h2>
        <p className='text-text-subtle mt-1 text-[11px] leading-5'>
          Screenata is creating the fixed account snapshot for this review. You
          can stay here; this page updates automatically.
        </p>
      </div>
      <div className='mx-auto mt-6 max-w-2xl space-y-2' aria-hidden='true'>
        {[0, 1, 2].map((row) => (
          <div
            key={row}
            className='border-border flex animate-pulse items-center gap-3 rounded-md border px-3 py-3'
          >
            <div className='bg-surface-subtle h-7 w-7 rounded-full' />
            <div className='flex-1 space-y-1.5'>
              <div className='bg-surface-subtle h-2.5 w-2/5 rounded' />
              <div className='bg-surface-subtle h-2 w-3/5 rounded' />
            </div>
          </div>
        ))}
      </div>
    </section>
  )
}

function DecisionHistory({ itemId }: { itemId: string }) {
  const query = useQuery(trpc.accessReview.itemHistory.queryOptions({ itemId }))
  if (query.isLoading)
    return <p className='text-text-subtle text-[10px]'>Loading history…</p>
  if (!query.data?.length)
    return <p className='text-text-subtle text-[10px]'>No prior decisions.</p>
  return (
    <div className='space-y-2'>
      {query.data.map((entry, index) => {
        const metadata = (entry.metadata ?? {}) as Record<string, unknown>
        return (
          <div
            key={`${entry.timestamp}-${index}`}
            className='text-text-body text-[10px]'
          >
            <span className='font-medium'>
              {typeof metadata.toDecision === 'string'
                ? decisionLabel(metadata.toDecision)
                : 'Updated'}
            </span>
            {' · '}
            {entry.actorName ?? entry.actorType ?? 'Reviewer'}
            {' · '}
            {format(new Date(entry.timestamp), 'MMM d, yyyy HH:mm')}
            {typeof metadata.notes === 'string' && metadata.notes ? (
              <p className='text-text-subtle mt-0.5'>{metadata.notes}</p>
            ) : null}
          </div>
        )
      })}
    </div>
  )
}

function ReviewStat({
  label,
  value,
  qualifier,
  selected,
  onSelect,
}: {
  label: string
  value: number
  qualifier?: string
  selected: boolean
  onSelect: () => void
}) {
  return (
    <button
      type='button'
      aria-pressed={selected}
      aria-label={`${label}: ${value}${qualifier ? ` ${qualifier}` : ''}`}
      className={`rounded-md border px-4 py-3 text-left transition-[border-color,background-color,box-shadow,transform] duration-150 outline-none hover:bg-surface-subtle focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 active:scale-[0.99] ${
        selected
          ? 'border-ring bg-selected ring-1 ring-ring'
          : 'border-border bg-card'
      }`}
      onClick={onSelect}
    >
      <div className='text-text-subtle text-[10px] font-semibold tracking-[0.05em] uppercase'>
        {label}
      </div>
      <div className='text-text-secondary mt-1 flex items-baseline gap-1.5 text-[22px] leading-none font-semibold tabular-nums'>
        {value}
        {qualifier ? (
          <span className='text-text-subtle text-[12px] font-medium'>
            {qualifier}
          </span>
        ) : null}
      </div>
    </button>
  )
}

const TIER_BADGE_STATUS: Record<
  AccessReviewPrivilegeTier,
  'red' | 'green' | 'blue' | 'default'
> = { ADMIN: 'red', VIEWER: 'green', USER: 'blue', UNKNOWN: 'default' }

function PrivilegeTierBadge({ tier }: { tier: AccessReviewPrivilegeTier }) {
  return (
    <Badge status={TIER_BADGE_STATUS[tier]} className='px-1.5 py-0 text-[8px]'>
      {tier}
    </Badge>
  )
}

function LastActive({ lastActiveAt }: { lastActiveAt: Date | string | null }) {
  if (!lastActiveAt) return null
  const date = new Date(lastActiveAt)
  return <span>Last active {format(date, 'MMM d, yyyy')}</span>
}

type AccountKind = 'EMPLOYEE' | 'SERVICE' | 'TEST' | 'UNKNOWN'

const ACCOUNT_KIND_LABEL: Record<AccountKind, string> = {
  EMPLOYEE: 'Employee',
  SERVICE: 'Service account',
  TEST: 'Test account',
  UNKNOWN: 'Unknown',
}

// One tone per kind so a column of badges reads at a glance: people in ink,
// non-human identities in cobalt, sandbox accounts in amber, unresolved in gray.
const ACCOUNT_KIND_BADGE_CLASS: Record<AccountKind, string> = {
  EMPLOYEE: 'border-border-surface bg-surface text-text-secondary',
  SERVICE: 'border-cobalt-border bg-selected text-cobalt-strong',
  TEST: 'border-status-amber-border bg-status-amber-subtle text-status-amber-ink',
  UNKNOWN: 'border-border-surface bg-surface-muted text-text-body',
}

function accountKind(item: AccessReviewItemDTO): AccountKind {
  if (isTestAccount(item)) return 'TEST'
  if (item.serviceAccount || item.principalType === 'SERVICE_ACCOUNT') {
    return 'SERVICE'
  }
  if (item.principalType === 'UNKNOWN') return 'UNKNOWN'
  return 'EMPLOYEE'
}

function isTestAccount(item: AccessReviewItemDTO) {
  return (
    item.serviceAccount?.isTestAccount === true ||
    item.employee?.complianceExclusionReason === 'TEST_ACCOUNT'
  )
}

function effectivePriority(item: AccessReviewItemDTO) {
  return isTestAccount(item) ? 'P1' : item.priority
}

function isRoutine(item: AccessReviewItemDTO) {
  return (
    item.ruleMatched === 'unchanged' &&
    item.privilegeTier !== 'ADMIN' &&
    item.principalType === 'EMPLOYEE' &&
    !isTestAccount(item) &&
    !item.serviceAccount
  )
}

function ruleLabel(rule: string | null | undefined) {
  switch (rule) {
    case 'terminated':
      return 'Terminated but account remains active'
    case 'sod_conflict':
      return 'Separation-of-duties conflict'
    case 'dormant_admin':
      return 'Dormant privileged account'
    case 'service_account':
      return 'Owner and purpose must be confirmed'
    case 'orphan_account':
      return 'No matching workforce identity'
    case 'role_mismatch':
      return 'Access differs from assigned role'
    case 'privileged':
      return 'Privileged access'
    case 'new_since_last':
      return 'New since last review'
    default:
      return null
  }
}

function changeLabel(change: string | null | undefined) {
  if (!change || change === 'no_change') return null
  return change.replaceAll('_', ' ')
}

function accessLevelLabel(accessLevel: string | null | undefined) {
  return !accessLevel || accessLevel.trim().toLowerCase() === 'unknown'
    ? 'Access level unavailable'
    : accessLevel
}

/** Colour the suggestion like the button it points to. */
function recommendationStatus(
  recommendation: string
): 'green' | 'red' | 'amber' | undefined {
  switch (recommendation) {
    case 'REVOKE':
      return 'red'
    case 'INVESTIGATE':
      return 'amber'
    case 'OUT_OF_SCOPE':
      return undefined
    default:
      return 'green'
  }
}

function recommendationLabel(recommendation: string) {
  switch (recommendation) {
    case 'REVOKE':
      return 'Deny'
    // "Manager confirm" is a human-in-the-loop certify; name the button.
    case 'MANAGER_CONFIRM':
      return 'Certify'
    case 'INVESTIGATE':
      return 'Investigate'
    case 'OUT_OF_SCOPE':
      return 'Out of scope'
    default:
      return 'Certify'
  }
}

function decisionLabel(decision: string) {
  if (decision === 'REVOKE') return 'Denied'
  if (decision === 'ACCEPT_RISK') return 'Risk accepted'
  return decision.replaceAll('_', ' ')
}

function PageMessage({ children }: { children: string }) {
  return (
    <div className='text-text-subtle px-6 py-14 text-center text-[12px]'>
      {children}
    </div>
  )
}
